import {
  Trash2,
  Hourglass,
  BadgeCheck,
  Edit,
  Info,
  ChevronDown,
  Ban,
  Shield,
  ShieldOff,
  Power,
  PowerOff,
} from "lucide-react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import Cookies from "js-cookie"; // Add this import
import {
  deleteMember,
  updateMemberStatus,
  updateMemberAuthStatus,
} from "../../../services/memberServices";
import DeleteMemberModal from "./DeleteMemberModal";

const STATUS_OPTIONS = ["approved", "pending", "rejected"];

const statusIcons = {
  approved: <BadgeCheck size={14} className="text-green" />,
  pending: <Hourglass size={14} className="text-yellow-600" />,
  rejected: <Ban size={14} className="text-red" />,
};

const statusStyles = {
  approved: "bg-green text-white",
  pending: "bg-yellow-500 text-white",
  rejected: "bg-red text-white",
};

const StatusDropdown = ({ currentStatus, onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (status) => {
    setOpen(false);
    if (status !== currentStatus && !disabled) {
      onChange(status);
    }
  };

  // If disabled, show as read-only
  if (disabled) {
    return (
      <span
        className={`capitalize font-semibold text-xs px-2 py-1 rounded-full ${statusStyles[currentStatus]}`}
      >
        {currentStatus}
      </span>
    );
  }

  return (
    <div className="relative inline-block">
      <div
        onClick={() => setOpen((prev) => !prev)}
        className={`text-xs font-semibold rounded-full px-2 py-1 cursor-pointer flex items-center justify-center gap-1 ${statusStyles[currentStatus]}`}
      >
        <span className="capitalize">{currentStatus}</span>
        <ChevronDown size={12} />
      </div>

      {open && (
        <div className="absolute z-10 mt-1 bg-white shadow-md rounded border w-32 flex flex-col">
          {STATUS_OPTIONS.map((status) => (
            <div
              key={status}
              onClick={() => handleSelect(status)}
              className={`px-2 py-[6px] text-xs cursor-pointer flex items-center gap-2 hover:bg-gray-100 ${
                currentStatus === status ? "font-bold" : ""
              }`}
            >
              {statusIcons[status]}
              <span className="capitalize py-1">{status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Updated Toggle Switch Component
const AuthStatusToggle = ({ currentStatus, onChange, disabled = false }) => {
  const [isToggling, setIsToggling] = useState(false);
  const isActive = currentStatus === "active";

  const handleToggle = async () => {
    if (isToggling || disabled) return;

    setIsToggling(true);
    const newStatus = isActive ? "inactive" : "active";

    try {
      await onChange(newStatus);
    } finally {
      setTimeout(() => setIsToggling(false), 300);
    }
  };

  // If disabled, show as read-only
  if (disabled) {
    return (
      <div className="flex items-center gap-2">
        <span
          className={`text-xs font-medium px-2 py-1 rounded-full ${
            isActive ? "bg-green text-white" : "bg-red text-white"
          }`}
        >
          {isActive ? "Active" : "Inactive"}
        </span>
        <div
          className={`w-2 h-2 rounded-full ${isActive ? "bg-green" : "bg-red"}`}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="relative group">
        {/* Toggle Switch */}
        <button
          onClick={handleToggle}
          disabled={isToggling || disabled}
          className={`
            relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 ease-in-out
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
            ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
            ${isActive ? "bg-green hover:bg-green" : "bg-red hover:bg-red"}
            ${isToggling ? "opacity-70" : ""}
          `}
        >
          {/* Toggle Circle */}
          <span
            className={`
              inline-block h-4 w-4 transform rounded-full bg-white transition-all duration-300 ease-in-out
              flex items-center justify-center shadow-sm
              ${isActive ? "translate-x-6" : "translate-x-1"}
              ${isToggling ? "scale-90" : "scale-100"}
            `}
          >
            {/* Icon inside the toggle circle */}
            {!isToggling && (
              <div
                className={`transition-all duration-300 ${
                  isActive ? "text-green" : "text-red"
                }`}
              >
                {isActive ? <Power size={10} /> : <PowerOff size={10} />}
              </div>
            )}
          </span>
        </button>

        {/* Enhanced Tooltip */}
        {!disabled && (
          <div className="absolute z-30 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 transform scale-95 group-hover:scale-100 pointer-events-none">
            <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap top-full left-1/2 transform -translate-x-1/2 mt-2 absolute">
              <div className="font-medium mb-1">
                Click to {isActive ? "deactivate" : "activate"}
              </div>
              <div className="text-gray-300 text-[10px]">
                {isActive
                  ? "Member can access all features"
                  : "Member access is suspended"}
              </div>
              {/* Tooltip arrow */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-gray-900"></div>
            </div>
          </div>
        )}
      </div>

      {/* Status Text */}
      <span
        className={`text-xs font-medium ${
          isActive ? "text-green" : "text-red"
        }`}
      >
        {isActive ? "Active" : "Inactive"}
      </span>

      {/* Status indicator dot */}
      <div className="relative">
        <div
          className={`
            w-2 h-2 rounded-full transition-all duration-300
            ${
              isActive
                ? "bg-green shadow-lg shadow-green-500/30"
                : "bg-red shadow-lg shadow-red-500/30"
            }
            ${isActive ? "animate-pulse" : ""}
          `}
        />
        {isActive && (
          <div className="absolute inset-0 w-2 h-2 bg-green-400 rounded-full animate-ping opacity-30"></div>
        )}
      </div>
    </div>
  );
};

export const MemberListTable = ({
  members,
  refetch,
  startIndex,
  isHide = false,
  permissionsData,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  console.log(permissionsData, "permissionsData");

  const role = Cookies.get("role");

  let canEdit = false;
  let canDelete = false;
  let canUpdateStatus = false;
  let canUpdateAuthStatus = false;

  if (role === "admin") {
    canEdit = true;
    canDelete = true;
    canUpdateStatus = true;
    canUpdateAuthStatus = true;
  } else if (role === "maintainer") {
    const modulePermissions = permissionsData?.permissions?.find(
      (p) => p.module === "Members"
    );

    console.log(modulePermissions, "modulePermissions");

    canEdit = !!modulePermissions?.edit;
    canDelete = !!modulePermissions?.delete;
    canUpdateStatus = !!modulePermissions?.edit; // Assuming status update requires edit permission
    canUpdateAuthStatus = !!modulePermissions?.edit; // Assuming auth status update requires edit permission
  }

  const showActions = canEdit || canDelete;

  const handleStatusUpdate = async (id, newStatus) => {
    if (!canUpdateStatus) {
      toast.error("You don't have permission to update status");
      return;
    }

    try {
      await updateMemberStatus(id, newStatus);
      toast.success("Member status updated");
      refetch();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAuthStatusUpdate = async (id, newAuthStatus) => {
    if (!canUpdateAuthStatus) {
      toast.error("You don't have permission to update auth status");
      return;
    }

    try {
      await updateMemberAuthStatus(id, newAuthStatus);
      toast.success(
        `Member access ${
          newAuthStatus === "active" ? "activated" : "suspended"
        }`,
        {
          icon: newAuthStatus === "active" ? "✅" : "🚫",
          duration: 3000,
        }
      );
      refetch();
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  const confirmDelete = (member) => {
    if (!canDelete) {
      toast.error("You don't have permission to delete members");
      return;
    }
    setSelectedMember(member);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteMember(selectedMember._id);
      toast.success("Member deleted");
      setShowDeleteModal(false);
      setSelectedMember(null);
      refetch();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Updated function to handle the hyphen display
  const getVerificationStatus = (isVerified) => {
    if (isVerified) {
      return {
        icon: <BadgeCheck size={14} className="text-green" />,
        label: "Verified",
        display: true,
      };
    } else {
      return {
        icon: null,
        label: "-",
        display: true,
      };
    }
  };

  return (
    <>
      <div className="bg-white rounded-t-2xl overflow-auto shadow-sm min-h-[300px]">
        <table className="w-full text-sm border border-gray-300 table-auto">
          <thead className="bg-[#4F22D8] text-white text-sm !rounded-t-2xl">
            <tr>
              <th className="py-3 px-2 text-left font-bold border-r border-gray-400">
                No
              </th>
              <th className="py-2 px-2 text-left font-bold border-r border-gray-400">
                Name
              </th>
              <th className="py-2 px-2 text-left font-bold border-r border-gray-400">
                Membership ID
              </th>
              <th className="py-2 px-2 text-left font-bold border-r border-gray-400">
                Mail ID
              </th>
              <th className="py-2 px-2 text-left font-bold border-r border-gray-400">
                Mobile
              </th>
              <th className="py-2 px-2 text-left font-bold border-r border-gray-400">
                Designation
              </th>
              <th className="py-2 px-2 text-left font-bold border-r border-gray-400">
                Status
              </th>
              <th className="py-2 px-2 text-left font-bold border-r border-gray-400">
                Auth Status
              </th>
              {/* Conditionally render Actions header */}
              {showActions && (
                <th className="py-2 px-2 text-left font-bold">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr>
                <td
                  colSpan={showActions ? "9" : "8"}
                  className="text-center py-6 text-grey"
                >
                  No members found.
                </td>
              </tr>
            ) : (
              members.map((member, idx) => (
                <tr
                  key={member._id}
                  className="border-t border-gray-300 hover:bg-gray-50"
                >
                  <td className="px-2 py-2 border-r border-gray-300">
                    {startIndex + idx + 1}
                  </td>
                  <td className="px-2 py-2 border-r border-gray-300 break-words">
                    {member.fullName}
                  </td>
                  <td className="px-2 py-2 border-r text-center border-gray-300 break-words">
                    {member.membershipId ?? "-"}
                  </td>
                  <td className="px-2 py-2 border-r border-gray-300 break-words">
                    <div className="flex flex-col gap-0.5">
                      <span>{member.email}</span>
                      <span
                        className={`text-xs font-bold flex items-center gap-1 ${
                          member.isEmailVerified
                            ? "text-green"
                            : "text-gray-500"
                        }`}
                      >
                        {member.isEmailVerified ? (
                          <>
                            {getVerificationStatus(member.isEmailVerified).icon}
                            {
                              getVerificationStatus(member.isEmailVerified)
                                .label
                            }
                          </>
                        ) : (
                          "-"
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-2 border-r border-gray-300 break-words">
                    <div className="flex flex-col gap-0.5">
                      <span>{member.mobile}</span>
                      <span
                        className={`text-xs font-bold flex items-center gap-1 ${
                          member.isMobileVerified
                            ? "text-green"
                            : "text-gray-500"
                        }`}
                      >
                        {member.isMobileVerified ? (
                          <>
                            {
                              getVerificationStatus(member.isMobileVerified)
                                .icon
                            }
                            {
                              getVerificationStatus(member.isMobileVerified)
                                .label
                            }
                          </>
                        ) : (
                          "-"
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-2 border-r border-gray-300">
                    {member.currentDesignationName ?? "-"}
                  </td>
                  <td className="px-2 py-2 border-r border-gray-300">
                    {isHide || !canUpdateStatus ? (
                      <span
                        className={`capitalize font-semibold text-xs px-2 py-1 rounded-full ${
                          statusStyles[member.status]
                        }`}
                      >
                        {member.status}
                      </span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <StatusDropdown
                          currentStatus={member.status}
                          onChange={(newStatus) =>
                            handleStatusUpdate(member._id, newStatus)
                          }
                          disabled={!canUpdateStatus}
                        />
                        <div className="relative group">
                          <Info
                            size={14}
                            className="text-gray-400 hover:text-gray-600"
                          />
                          <div className="absolute z-20 hidden group-hover:block w-max bg-black text-white text-xs px-1.5 py-0.5 rounded top-full left-1/2 transform -translate-x-1/2 mt-1 whitespace-nowrap">
                            Please update status
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 border-r border-gray-300">
                    <AuthStatusToggle
                      currentStatus={member.memberAuthStatus || "active"}
                      onChange={(newAuthStatus) =>
                        handleAuthStatusUpdate(member._id, newAuthStatus)
                      }
                      disabled={isHide || !canUpdateAuthStatus}
                    />
                  </td>
                  {showActions && (
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            const title = encodeURIComponent(
                              member.fullName.replace(/\s+/g, "-")
                            );
                            window.location.href = `/admin/members/detail/${member._id}/${title}`;
                          }}
                          className="text-purple-700 font-semibold text-xs flex items-center gap-1"
                        >
                          <Info size={14} /> View
                        </button>

                        {canEdit && (
                          <>
                            <div className="w-px h-4 bg-gray-300" />
                            <button
                              onClick={() => {
                                window.location.href = `/admin/members/edit-member/${member._id}`;
                              }}
                              className="text-[#4F22D8] font-semibold text-xs flex items-center gap-1"
                            >
                              <Edit size={14} /> Edit
                            </button>
                          </>
                        )}

                        {canDelete && (
                          <>
                            <div className="w-px h-4 bg-gray-300" />
                            <button
                              onClick={() => confirmDelete(member)}
                              className="text-[#D40404] font-semibold text-xs flex items-center gap-1"
                            >
                              <Trash2 size={14} /> Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedMember && (
        <DeleteMemberModal
          isOpen={showDeleteModal}
          onRequestClose={() => setShowDeleteModal(false)}
          onDelete={handleDelete}
          itemName={`${selectedMember.fullName}`}
          itemType="member"
        />
      )}
    </>
  );
};
